<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'next/icon/Icon.vue';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { accountId } = useAccount();

const tierData = ref(null);
const loading = ref(false);

const TIER_EMOJI = {
  level_1: '🎟️',
  level_2: '🎪',
  level_3: '📋',
  level_4: '📅',
  level_5: '🎬',
  level_6: '🎭',
  level_7: '🎼',
  level_8: '🏆',
  level_9: '♟️',
  level_10: '🌟',
  level_11: '👑',
  level_12: '⚡',
};

const TIER_COLORS = {
  level_1: 'bg-stone-100 text-stone-700',
  level_2: 'bg-sky-100 text-sky-700',
  level_3: 'bg-blue-100 text-blue-700',
  level_4: 'bg-cyan-100 text-cyan-700',
  level_5: 'bg-teal-100 text-teal-700',
  level_6: 'bg-emerald-100 text-emerald-700',
  level_7: 'bg-violet-100 text-violet-700',
  level_8: 'bg-orange-100 text-orange-700',
  level_9: 'bg-amber-100 text-amber-800',
  level_10: 'bg-indigo-100 text-indigo-700',
  level_11: 'bg-rose-100 text-rose-700',
  level_12: 'bg-yellow-100 text-yellow-800',
};

const optedIn = computed({
  get: () => uiSettings.value.planning_tier_opted_in === true,
  set: async value => {
    await updateUISettings({ planning_tier_opted_in: value });
    useAlert(
      value
        ? t('PROFILE_SETTINGS.FORM.PLANNING_TIER.OPT_IN_SUCCESS')
        : t('PROFILE_SETTINGS.FORM.PLANNING_TIER.OPT_OUT_SUCCESS')
    );
    if (value) fetchTier();
  },
});

const tier = computed(() => tierData.value?.tier || null);
const certificates = computed(() => tierData.value?.certificates || []);
const formattedRevenue = computed(() => {
  const amount = tier.value?.total_revenue ?? 0;
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);
});
const tierEmoji = computed(() =>
  tier.value ? TIER_EMOJI[tier.value.tier_level] || '🎟️' : '🎟️'
);
const tierName = computed(() =>
  tier.value
    ? t(`PROFILE_SETTINGS.FORM.PLANNING_TIER.LEVELS.${tier.value.tier_level}`)
    : ''
);
const tierColor = computed(() =>
  tier.value
    ? TIER_COLORS[tier.value.tier_level] || 'bg-stone-100 text-stone-700'
    : 'bg-stone-100 text-stone-700'
);

async function fetchTier() {
  loading.value = true;
  try {
    const { data } = await axios.get('/api/v1/profile/planning_tier', {
      params: { account_id: accountId.value },
    });
    tierData.value = data;
  } catch {
    // Tier data not yet available
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (optedIn.value) fetchTier();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Opt-in toggle -->
    <div class="flex items-center justify-between gap-4 py-2">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.TOGGLE_TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-10">
          {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.TOGGLE_NOTE') }}
        </p>
      </div>
      <Switch v-model="optedIn" />
    </div>

    <!-- Tier card — visible when opted in -->
    <template v-if="optedIn">
      <div v-if="loading" class="text-xs text-n-slate-10 py-2">
        {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.LOADING') }}
      </div>

      <div
        v-else-if="tier"
        class="rounded-xl border border-n-weak p-4 flex items-center gap-4"
      >
        <div
          class="w-14 h-14 rounded-full flex items-center justify-center text-3xl flex-shrink-0"
          :class="tierColor"
        >
          {{ tierEmoji }}
        </div>
        <div class="min-w-0 flex-1">
          <p
            class="text-xs text-n-slate-10 uppercase tracking-wide font-semibold"
          >
            {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.CURRENT_TIER') }}
          </p>
          <p class="text-lg font-bold text-n-slate-12 leading-tight">
            {{ tierName }}
          </p>
          <div class="flex gap-4 mt-1 text-xs text-n-slate-10">
            <span>{{
              $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.EVENTS', {
                count: tier.events_count,
              })
            }}</span>
            <span>{{
              $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.REVENUE', {
                amount: formattedRevenue,
              })
            }}</span>
            <span>{{
              $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.SCORE', {
                score: tier.score,
              })
            }}</span>
          </div>
        </div>
        <button
          class="flex-shrink-0 p-1.5 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
          :title="$t('PROFILE_SETTINGS.FORM.PLANNING_TIER.REFRESH')"
          @click="fetchTier"
        >
          <Icon icon="i-lucide-refresh-cw" class="size-4" />
        </button>
      </div>

      <div
        v-else-if="!loading"
        class="rounded-xl border border-dashed border-n-weak p-4 text-center text-xs text-n-slate-10"
      >
        {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.NO_TIER_YET') }}
      </div>

      <!-- Certificates -->
      <div v-if="certificates.length > 0" class="flex flex-col gap-2">
        <p
          class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
        >
          {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.CERTIFICATES') }}
        </p>
        <div
          v-for="cert in certificates"
          :key="cert.certificate_uid"
          class="flex items-center justify-between gap-3 rounded-lg border border-n-weak px-3 py-2"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-lg">{{
              TIER_EMOJI[cert.tier_level] || '🎟️'
            }}</span>
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{
                  $t(
                    `PROFILE_SETTINGS.FORM.PLANNING_TIER.LEVELS.${cert.tier_level}`
                  )
                }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ new Date(cert.issued_at).toLocaleDateString() }}
              </p>
            </div>
          </div>
          <div class="flex gap-2 flex-shrink-0">
            <a
              :href="cert.public_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs text-n-brand hover:underline"
            >
              {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.VIEW') }}
            </a>
            <a
              :href="cert.linkedin_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs text-[#0A66C2] hover:underline"
            >
              {{ $t('PROFILE_SETTINGS.FORM.PLANNING_TIER.LINKEDIN') }}
            </a>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
